<template>
    <div class="mc-select">
        <Multiselect
            ref="multiselect"
            v-model="selected"
            v-select-overflow="shouldUseDirective"
            v-bind="selectSettings"
            :options="filteredOptions"
            :disabled="disabled"
            :class="{ 'multiselect--error-border': hasError }"
            :placeholder="placeholder"
            :show-no-results="showNoResults"
            @input="emitValue"
            @search-change="handleSearch"
        >
            <template #option="{ option }">
                <slot name="selectOption" :option="option" />
                <div v-if="option.separator" class="multiselect__separator" />
            </template>
            <template #singleLabel="{ option }">
                <slot name="selectSingleLabel" :option="option" />
            </template>
        </Multiselect>
    </div>
</template>

<script>
import Multiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.min.css';
import { mapGetters } from 'vuex';

export default {
    name: 'McSelect',
    components: {
        Multiselect,
    },
    props: {
        value: {
            type: [String, Number, Object],
            default: null,
        },
        presetValue: {
            type: Object,
            default: null,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        hasError: {
            type: Boolean,
            default: false,
        },
        placeholder: {
            type: String,
            default: '',
        },
        labelKey: {
            type: String,
            default: 'label',
        },
        trackBy: {
            type: String,
            default: 'key',
        },
        emitWholeValue: {
            type: Boolean,
            default: false,
        },
        // Must be an array of objects
        options: {
            type: Array,
            default: () => [],
            validator(value) {
                // Check if all items in the array are objects
                return value.every(item => typeof item === 'object' && !Array.isArray(item) && item !== null);
            },
        },
        openDirection: {
            type: String,
            default: 'bottom',
            validator: value => ['top', 'bottom', 'auto'].includes(value),
        },
        enableDirective: {
            type: Boolean,
            default: true,
        },
        searchable: {
            type: Boolean,
            default: false,
        },
        showNoResults: {
            type: Boolean,
            default: true,
        },
        useInternalSearch: {
            type: Boolean,
            default: true,
        },
    },
    data() {
        return {
            selected: this.presetValue,
            searchQuery: '',
            selectSettings: {
                showLabels: false,
                allowEmpty: false,
                searchable: this.searchable,
                clearOnSelect: false,
                internalSearch: this.useInternalSearch,
                openDirection: this.openDirection,
                optionHeight: 48, // important to set if we use keyboard navigation in dropdown
                trackBy: this.trackBy,
                label: this.labelKey,
            },
        };
    },
    computed: {
        ...mapGetters(['isLargeScreen']),
        shouldUseDirective() {
            return this.isLargeScreen && this.enableDirective;
        },
        filteredOptions() {
            if (!this.searchQuery) {
                return this.options;
            }

            const filtered = this.options.filter(option => {
                return option[this.labelKey]?.toLowerCase().includes(this.searchQuery.toLowerCase());
            });

            if (filtered.length === 0) {
                const noResultOption = this.options.find(opt => opt.isNoResultOption);

                return noResultOption ? [noResultOption] : [];
            }

            return filtered;
        },
    },
    watch: {
        value: {
            immediate: true,
            handler(newVal) {
                const trackValue = this.getIdentifier(newVal);
                this.selected = this.options.find(option => option[this.trackBy] === trackValue);
            },
        },
    },
    methods: {
        isObject(value) {
            return typeof value === 'object' && value !== null && !Array.isArray(value);
        },
        /**
         * Gets the unique identifier from an option.
         * Returns the tracking property (e.g., 'key') if option is an object,
         * or returns the value as-is if it's a primitive.
         */
        getIdentifier(option) {
            if (!option) {
                return null;
            }

            return this.isObject(option) ? option[this.trackBy] : option;
        },
        emitValue(selectedOption) {
            const emitValue = this.emitWholeValue ? selectedOption : this.getIdentifier(selectedOption);
            this.$emit('input', emitValue);
        },
        handleSearch(query) {
            this.searchQuery = query;
        },
    },
};
</script>

<style lang="scss" scoped>
@import '@/sass/_variables.scss';
$min-height: 47px;
$option-y-padding: 12px;
$option-x-padding: 16px;

.mc-select {
    position: relative;
    min-height: $min-height;
    @include font-size(16px);

    ::v-deep .multiselect {
        @include font-size(16px);
        min-height: $min-height;
        border-radius: $default-border-radius;
        color: $primary;

        &__tags {
            border: 1px solid $border-color;
            border-radius: $default-border-radius;
            padding: 12px 40px 0 8px;
            min-height: $min-height;
            @include font-size(16px);
        }

        &--error-border .multiselect__tags {
            border-color: $error;
        }

        &--disabled {
            opacity: 0.7;

            .multiselect__tags {
                background-color: $gray-light;
                color: $primary;
            }
        }

        &--active:not(.multiselect--above) {
            .multiselect__tags {
                border-color: $primary;
                border-radius: $default-border-radius;
            }
        }

        &__content-wrapper {
            border: 1px solid $border-color;
            border-radius: $default-border-radius;
            margin-top: 4px;
        }

        &__placeholder {
            padding: 0 0 0 8px;
            color: $secondary-text-color;
        }

        &__select {
            height: $min-height;
            border-bottom-right-radius: $default-border-radius;
            border-top-right-radius: $default-border-radius;
            background-color: transparent !important;

            &:before {
                content: '';
                position: absolute;
                top: -4px;
                width: 0;
                height: 0;
                right: 20px;
                bottom: 0;
                margin: auto;
                pointer-events: none;
                border: solid $primary;
                border-width: 0 3px 3px 0;
                display: inline-block;
                padding: 3px;
                transform: rotate(45deg);
                -webkit-transform: rotate(45deg);
            }
        }

        &--active .multiselect__select {
            transform: none;
        }

        &__option {
            padding: $option-y-padding $option-x-padding;
            cursor: pointer;

            &--selected {
                background-color: transparent;
                font-weight: normal;
            }

            &--highlight,
            &:hover,
            &--selected.multiselect__option--highlight {
                background-color: $bright-blue-light;
                color: $primary;
            }

            &--disabled {
                cursor: not-allowed;
                color: $disabled !important;
                background: transparent !important;
            }

            .other-brand-option {
                display: block;
                padding: $option-y-padding $option-x-padding;
                margin: -#{$option-y-padding} -#{$option-x-padding};
            }
        }

        &__single,
        &__option {
            .svg-icon {
                width: 24px;
                height: 24px;
                margin-right: 8px;
            }
        }

        &__single {
            padding-left: 8px;
            background-color: transparent !important;
        }

        &__separator {
            position: absolute;
            left: 0;
            right: 0;
            bottom: 0;
            height: 1px;
            background-color: $border-color;
        }
    }
}
</style>
